import immutable from 'object-path-immutable';
import {
  FETCH_PRODUCTS_BY_QUERY,
  FetchProductsByQueryAction,
} from '../actions/product.actionTypes';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { LOGOUT, LogoutAction } from '../actions/auth.actionTypes';
import {
  CourseMindItemSearchResult,
  MindItemSearchResult,
} from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { FETCH_NEW_COURSES } from '../actions/course.actionTypes';
import { FetchNewCoursesAction } from '../actions/course.actionTypes';
import {
  FETCH_DASHBOARD_WORKSHEETS,
  FetchDashboardWorksheetsAction,
} from '../actions/dashboard.actionTypes';
import { PIN_MIND_ITEM, UNPIN_DOCUMENT } from '../actions/pinned.actionTypes';

import { PinMindItemAction, UnpinDocumentAction } from '../actions/pinned.actionTypes';

type ActionTypes =
FetchProductsByQueryAction | LogoutAction | FetchNewCoursesAction | FetchDashboardWorksheetsAction
  | PinMindItemAction | UnpinDocumentAction;

export interface DashboardInitialStateType {
  productsBySubjectId: {
    [key: string]: {
      ids: string[];
      error?: string;
      status: AsyncActionStatus;
    };
  };
  newCourses: {
    data: CourseMindItemSearchResult[];
    status: AsyncActionStatus;
  };
  dashboardWorksheets: {
    data: MindItemSearchResult[];
    status: AsyncActionStatus;
    error?: string;
    // Joined subject key the stored data belongs to; lets the row refetch on a
    // subject change while skipping a remount that already holds the same set.
    subjectsKey: string;
  };
}

const initialState: DashboardInitialStateType = {
  productsBySubjectId: {},
  newCourses: {
    data: [],
    status: AsyncActionStatus.INITIAL,
  },
  dashboardWorksheets: {
    data: [],
    status: AsyncActionStatus.INITIAL,
    subjectsKey: '',
  },
};

const collections = (state = initialState, action: ActionTypes): DashboardInitialStateType => {
  switch (action.type) {
    case LOGOUT.SUCCESS:
      return initialState;

    case FETCH_PRODUCTS_BY_QUERY.START: {
      const { query } = action.payload;
      if (!query.subjects || query.subjects.length === 0) {
        return state;
      }

      let key: string = query.subjects.join(',');
      if (query.keyPrefix) {
        key = `${query.keyPrefix}-${key}`;
      }

      return immutable(state)
        .set(['productsBySubjectId', key, 'status'], AsyncActionStatus.LOADING)
        .set(['productsBySubjectId', key, 'ids'], [])
        .value();
    }
    case FETCH_PRODUCTS_BY_QUERY.ERROR: {
      const { query } = action.payload;
      if (!query.subjects || query.subjects.length === 0) {
        return state;
      }

      let key: string  = query.subjects.join(',');
      if (query.keyPrefix) {
        key = `${query.keyPrefix}-${key}`;
      }

      return immutable(state)
        .set(['productsBySubjectId', key, 'status'], AsyncActionStatus.ERROR)
        .value();
    }
    case FETCH_PRODUCTS_BY_QUERY.SUCCESS: {
      const { query } = action.payload;
      if (!query.subjects || query.subjects.length === 0) {
        return state;
      }
      
      let key: string  = query.subjects.join(',');
      if (query.keyPrefix) {
        key = `${query.keyPrefix}-${key}`;
      }

      return immutable(state)
        .set(['productsBySubjectId', key, 'status'], AsyncActionStatus.READY)
        .set(['productsBySubjectId', key, 'ids'], action.result.map(r => r.id))
        .value();
    }

    case FETCH_NEW_COURSES.START: {
      return immutable(state)
        .set('newCourses', {
          data: [],
          status: AsyncActionStatus.LOADING,
        })
        .value();
    }

    case FETCH_NEW_COURSES.ERROR: {
      return immutable(state)
        .set('newCourses', {
          data: [],
          status: AsyncActionStatus.ERROR,
        })
        .value();
    }
       
    case FETCH_NEW_COURSES.SUCCESS: {
      const { result } = action;

      return immutable(state)
        .set('newCourses', {
          data: result.data,
          status: AsyncActionStatus.READY,
        })
        .value();
    }

    case FETCH_DASHBOARD_WORKSHEETS.START: {
      return immutable(state)
        .set('dashboardWorksheets', {
          data: [],
          status: AsyncActionStatus.LOADING,
          subjectsKey: action.payload.subjects.join(','),
        })
        .value();
    }

    case FETCH_DASHBOARD_WORKSHEETS.ERROR: {
      // Ignore an out-of-order response whose subjects no longer match the latest
      // START — a newer request already owns the slot; clobbering it would surface
      // stale data and trigger a redundant refetch.
      if (action.payload.subjects.join(',') !== state.dashboardWorksheets.subjectsKey) {
        return state;
      }

      return immutable(state)
        .set('dashboardWorksheets', {
          data: [],
          status: AsyncActionStatus.ERROR,
          subjectsKey: action.payload.subjects.join(','),
        })
        .value();
    }

    case FETCH_DASHBOARD_WORKSHEETS.SUCCESS: {
      // Same stale-response guard as ERROR: a later START for different subjects
      // owns the slot, so drop this superseded result.
      if (action.payload.subjects.join(',') !== state.dashboardWorksheets.subjectsKey) {
        return state;
      }

      const seen = new Set<string>();
      const data = action.result.data.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });

      return immutable(state)
        .set('dashboardWorksheets', {
          data,
          status: AsyncActionStatus.READY,
          subjectsKey: action.payload.subjects.join(','),
        })
        .value();
    }

    case PIN_MIND_ITEM.SUCCESS: {
      const { id } = action.payload;

      const newCourses = state.newCourses.data.map((course) => {
        if (course.id === id) {
          return {
            ...course,
            isPinned: true,
          };
        }
        return course;
      });

      return immutable(state)
        .set('newCourses', {
          data: newCourses,
          status: AsyncActionStatus.READY,
        })
        .value();
    }

    case UNPIN_DOCUMENT.SUCCESS: {
      const { id } = action.payload;

      const newCourses = state.newCourses.data.map((course) => {
        if (course.id === id) {
          return {
            ...course,
            isPinned: false,
          };
        }
        return course;
      });

      return immutable(state)
        .set('newCourses', {
          data: newCourses,
          status: AsyncActionStatus.READY,
        })
        .value();
    }
  }

  return state;
};

export default collections;
